import { useEffect, useMemo, useState } from 'react';
import { fetchRecentResults, fetchUpcomingGames, type SportsApiGame } from '../services/sportsApi';
import type { LeagueGames, LiveGame } from '../types/index';

const LEAGUES = ['MLB', 'NBA', 'NFL', 'NHL'] as const;
const MAX_GAMES_PER_LEAGUE = 8;
const WINDOW_HOURS = 48;

interface LiveSportsState {
  leagues: LeagueGames[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  partialFailure: boolean;
  emptyReason: 'off_day' | 'unavailable' | null;
}

export interface UseLiveSportsReturn extends LiveSportsState {
  tickerItems: LiveGame[];
}

function kickoffFromGame(game: SportsApiGame): string {
  if (game.timestamp) return game.timestamp;
  if (game.date && game.time) return `${game.date}T${game.time}`;
  return game.date || '';
}

function mapToLiveGame(game: SportsApiGame): LiveGame {
  return {
    id: game.id,
    league: game.league,
    homeTeam: game.homeTeam,
    awayTeam: game.awayTeam,
    homeScore: game.homeScore,
    awayScore: game.awayScore,
    kickoff: kickoffFromGame(game),
    location: game.location,
    status: game.status,
  };
}

function sortGames(a: LiveGame, b: LiveGame): number {
  return compareLiveGames(a, b);
}

function parseKickoff(kickoff: string): Date | null {
  // Date-only strings ("2026-06-25") parse as UTC midnight per spec, which would
  // compare against the wrong local day for users in negative UTC offsets (Americas).
  // Force local-midnight parsing by appending T00:00:00 for bare date strings.
  const raw = /^\d{4}-\d{2}-\d{2}$/.test(kickoff) ? `${kickoff}T00:00:00` : kickoff;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function freshnessRank(game: LiveGame, now = new Date()): number {
  const kickoffDate = parseKickoff(game.kickoff);
  if (!kickoffDate) return 3;
  if (isSameLocalDay(kickoffDate, now)) return 0;
  return game.status === 'upcoming' ? 1 : 2;
}

export function compareLiveGames(a: LiveGame, b: LiveGame, now = new Date()): number {
  const rankA = freshnessRank(a, now);
  const rankB = freshnessRank(b, now);
  if (rankA !== rankB) return rankA - rankB;

  const aTime = Date.parse(a.kickoff);
  const bTime = Date.parse(b.kickoff);

  if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
  return a.status === 'upcoming' ? aTime - bTime : bTime - aTime;
}

export function isInActiveWindow(game: LiveGame, now = new Date()): boolean {
  const kickoffDate = parseKickoff(game.kickoff);
  if (!kickoffDate) return false;

  if (isSameLocalDay(kickoffDate, now)) return true;

  const windowMs = WINDOW_HOURS * 60 * 60 * 1000;
  const delta = kickoffDate.getTime() - now.getTime();

  if (game.status === 'upcoming') return delta > 0 && delta <= windowMs;
  return delta <= 0 && Math.abs(delta) <= windowMs;
}

export function useLiveSports(): UseLiveSportsReturn {
  const [state, setState] = useState<LiveSportsState>({
    leagues: [],
    loading: true,
    error: null,
    lastUpdated: null,
    partialFailure: false,
    emptyReason: null,
  });

  useEffect(() => {
    let isMounted = true;

    async function loadLiveSports() {
      setState((current) => ({ ...current, loading: true, error: null }));

      const leagueResults = await Promise.allSettled(
        LEAGUES.map(async (league) => {
          const [upcomingResult, recentResult] = await Promise.allSettled([
            fetchUpcomingGames(league),
            fetchRecentResults(league),
          ]);

          const upcoming = upcomingResult.status === 'fulfilled' ? upcomingResult.value : [];
          const recent = recentResult.status === 'fulfilled' ? recentResult.value : [];
          const games = [...upcoming, ...recent]
            .map(mapToLiveGame)
            .filter((game) => isInActiveWindow(game))
            .sort(sortGames)
            .slice(0, MAX_GAMES_PER_LEAGUE);

          return {
            league,
            games,
            failedCount:
              (upcomingResult.status === 'rejected' ? 1 : 0) +
              (recentResult.status === 'rejected' ? 1 : 0),
          };
        })
      );

      if (!isMounted) return;

      const leagues = leagueResults
        .filter(
          (result): result is PromiseFulfilledResult<LeagueGames & { failedCount: number }> =>
            result.status === 'fulfilled'
        )
        .map((result) => ({ league: result.value.league, games: result.value.games }))
        .filter((league) => league.games.length > 0);

      const failedCount =
        leagueResults.filter((result) => result.status === 'rejected').length +
        leagueResults.reduce(
          (count, result) =>
            result.status === 'fulfilled' ? count + result.value.failedCount : count,
          0
        );
      const partialFailure = failedCount > 0;
      const emptyReason = leagues.length === 0 ? (partialFailure ? 'unavailable' : 'off_day') : null;

      setState({
        leagues,
        loading: false,
        error: emptyReason === 'unavailable' ? 'Unable to load live sports data.' : null,
        lastUpdated: new Date().toISOString(),
        partialFailure,
        emptyReason,
      });
    }

    loadLiveSports();

    return () => {
      isMounted = false;
    };
  }, []);

  const tickerItems = useMemo(
    () => state.leagues.flatMap((league) => league.games).slice(0, 16),
    [state.leagues]
  );

  return {
    ...state,
    tickerItems,
  };
}
